import { Record, OrderedMap, Set, Map } from 'immutable'
import uuid from 'uuid'
import viewStateReducer from './viewState'

import {
  FETCH_VERSION_SUCCESS,
  FETCH_VERSION_ERROR,
} from 'data/config/actionTypes'

import { CHANNEL_PANEL_TABS } from './ChannelPanelTypes'

import { CONNECTION_STATUSES_TYPE } from './selectors'

import {
  CLOSE_ACTIVE_MODAL,
  SET_ACTIVE_ORGANIZATION_ID,
  SET_ACTIVE_USER_ID,
  SET_WARNING_MESSAGE,
  SET_ACTIVE_MODAL,
  SET_CONNECTION_STATUS,
  SET_SUBSCRIPTION_STATUS,
  CREATE_TOASTER_MESSAGE,
  DISMISS_TOASTER_MESSAGE,
  TOGGLE_FOCUS_MODE,
  SET_DETACHED_MODE,
  SET_OUTLINE_CONTAINER_ID,
  RESET_SELECTED_NOTE_IDS,
  COLLAPSE_SIDEBAR,
  UNCOLLAPSE_SIDEBAR,
  TOGGLE_CHANNELS_LIST,
  SET_ROUTE_PARAMS,
  START_DIRECT_EDITING_NOTE,
  STOP_DIRECT_EDITING_NOTE,
} from './actionTypes'

export const ToasterMessage = Record({
  id: null,
  type: null,
  title: null,
  detail: null,
  createdAt: null,
  dismissedAt: null,
  renderAction: null,
})

const ConnectionStatuses = Record({
  navigator: null,
  collab: null,
  graphql: null,
  ping: null,
})

const SubscritionStatuses = Map({
  latestAt: null,
})

const InitialState = Record({
  activeOrganizationId: null,
  activeUserId: null,
  activeChannelId: null,
  activeFilterId: null,
  activeNoteId: null,
  activeTemplateId: null,
  activeModal: null,
  warningMessage: null,
  activeThreadId: null,
  activeInlineThreadId: null,
  toasters: OrderedMap(),
  connectionStatuses: new ConnectionStatuses(),
  subscriptionStatuses: SubscritionStatuses,
  selectedNoteIds: Set(),
  expandedNoteIds: Set(),
  viewState: undefined,
  matchingContactUsers: Set(),
  isDetachedModeEnabled: false,
  hoveringNoteId: null,
  outlineContainerId: null,
  channelPanelTab: CHANNEL_PANEL_TABS[0],
  previousLocation: null,
  currentLocation: null,
  isSidebarCollapsed: false,
  isSidebarChannelsListActive: false,
  metaPanelType: null,
  routeParams: {},
  isDirectEditingNote: false,
})

const uiReducer = (state = new InitialState(), action) => {
  switch (action.type) {
    case SET_ACTIVE_ORGANIZATION_ID: {
      const { id } = action.payload
      return state.set('activeOrganizationId', id)
    }
    case SET_ACTIVE_USER_ID: {
      const { id } = action.payload
      return state.set('activeUserId', id)
    }
    case SET_ACTIVE_MODAL: {
      const { id, meta } = action.payload
      return state.set('activeModal', { id, meta })
    }
    case CLOSE_ACTIVE_MODAL: {
      return state.set('activeModal', null)
    }
    case SET_DETACHED_MODE: {
      return state.set('isDetachedModeEnabled', true)
    }
    case SET_WARNING_MESSAGE: {
      const { warningMessage } = action.payload
      return state.set('warningMessage', warningMessage)
    }
    case FETCH_VERSION_SUCCESS: {
      if (
        state.get('connectionStatuses').get('ping') ===
        CONNECTION_STATUSES_TYPE.CONNECTED
      ) {
        return state
      }
      return state.update('connectionStatuses', statuses =>
        statuses.set('ping', CONNECTION_STATUSES_TYPE.CONNECTED)
      )
    }
    case FETCH_VERSION_ERROR: {
      if (
        state.get('connectionStatuses').get('ping') ===
        CONNECTION_STATUSES_TYPE.DISCONNECTED
      ) {
        return state
      }
      return state.update('connectionStatuses', statuses =>
        statuses.set('ping', CONNECTION_STATUSES_TYPE.DISCONNECTED)
      )
    }
    case SET_CONNECTION_STATUS: {
      const { status, service } = action.payload
      return state.update('connectionStatuses', statuses =>
        statuses.set(service, status)
      )
    }
    case SET_SUBSCRIPTION_STATUS: {
      const { type } = action.payload
      return state.update('subscriptionStatuses', statuses =>
        statuses.set('lastAt', new Date()).set(type, new Date())
      )
    }
    case CREATE_TOASTER_MESSAGE: {
      const { title, detail, type, id = uuid(), renderAction } = action.payload

      return state.update('toasters', toasters =>
        toasters.set(
          id,
          new ToasterMessage({
            id,
            title,
            detail,
            type,
            createdAt: new Date().toISOString(),
            renderAction,
          })
        )
      )
    }
    case DISMISS_TOASTER_MESSAGE: {
      const { id } = action.payload
      return state.updateIn(['toasters', id], toaster =>
        toaster.merge({
          dismissedAt: new Date().toISOString(),
        })
      )
    }
    case COLLAPSE_SIDEBAR: {
      return state.set('isSidebarCollapsed', true)
    }
    case UNCOLLAPSE_SIDEBAR: {
      return state.set('isSidebarCollapsed', false)
    }
    case TOGGLE_FOCUS_MODE: {
      return state.set('isSidebarCollapsed', !state.get('isSidebarCollapsed'))
    }
    case TOGGLE_CHANNELS_LIST: {
      return state.set(
        'isSidebarChannelsListActive',
        !state.get('isSidebarChannelsListActive')
      )
    }
    case 'SEARCH/SWITCH_TO_FULL_TEXT': {
      // from data/search
      return state.set('isSidebarCollapsed', false)
    }
    case SET_OUTLINE_CONTAINER_ID: {
      return state.set('outlineContainerId', action.payload.containerId)
    }
    case RESET_SELECTED_NOTE_IDS: {
      return state.set('selectedNoteIds', Set())
    }

    case START_DIRECT_EDITING_NOTE: {
      return state.set('isDirectEditingNote', true)
    }

    case STOP_DIRECT_EDITING_NOTE: {
      return state.set('isDirectEditingNote', false)
    }

    case SET_ROUTE_PARAMS: {
      const {
        channelId,
        noteId,
        templateId,
        threadId,
        filterId,
        options,
      } = action.payload.params
      const isDetached = options === 'detached'
      const metaPanelType = !isDetached && options

      const activeNoteId = noteId || templateId
      const previousActiveNoteId = state.get('activeNoteId')
      const previousIsDirectEditingNote = state.get('isDirectEditingNote')

      return state
        .set('routeParams', action.payload.params)
        .set('activeNoteId', activeNoteId)
        .set('activeFilterId', filterId)
        .set('activeChannelId', channelId)
        .set('activeTemplateId', templateId)
        .set('activeThreadId', threadId)
        .set('metaPanelType', metaPanelType)
        .set('previousLocation', state.currentLocation)
        .set('currentLocation', action.payload.location)
        .set('selectedNoteIds', Set())
        .set(
          'isDirectEditingNote',
          previousActiveNoteId !== activeNoteId
            ? false
            : previousIsDirectEditingNote
        )
    }
    default: {
      return state.set('viewState', viewStateReducer(state.viewState, action))
    }
  }
}

export default uiReducer
